import React from 'react';
import { motion } from 'framer-motion';
import { Zap, RefreshCw, Mic, CalendarCheck } from 'lucide-react';

const features = [
  {
    title: 'Instant Response in < 2 Seconds',
    description: 'Your AI agent picks up the phone before the lead loses interest. Every second counts in real estate.',
    icon: Zap,
    span: 'md:col-span-8',
    hasImage: true,
  },
  {
    title: 'Seamless CRM Sync',
    description: 'Automatically pushes qualified leads, call notes, and appointments to Follow Up Boss, Sierra, and more.',
    icon: RefreshCw,
    span: 'md:col-span-4',
  },
  {
    title: 'Natural Local Accents',
    description: 'Voices trained to sound like a real Calgary local. No robotic tone, no uncanny valley.',
    icon: Mic,
    span: 'md:col-span-4',
  },
  {
    title: '24/7 Smart Outreach',
    description: 'Late-night or early-morning lead? Your AI agent knows the difference — it fires a text message instead of calling, because nobody wants a phone call at 2 AM. Always responsive, always respectful.',
    icon: CalendarCheck,
    span: 'md:col-span-8',
  },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

export default function FeaturesSection() {
  return (
    <section id="features" data-testid="features-section" className="py-24 lg:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-[#00E5FF] font-semibold block mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Features
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-white"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            The Ultimate Edge for<br />Real Estate Agents
          </h2>
        </motion.div>

        {/* Bento Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          className="grid grid-cols-1 md:grid-cols-12 gap-4"
        >
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                variants={cardVariants}
                data-testid={`feature-card-${feature.title.toLowerCase().replace(/\s+/g, '-').replace(/[<>]/g, '')}`}
                className={`${feature.span} bento-card bg-neutral-900/40 border border-white/5 rounded-2xl p-8 lg:p-10 relative overflow-hidden group`}
              >
                {/* Icon */}
                <div className="w-12 h-12 rounded-xl bg-[#00E5FF]/10 flex items-center justify-center mb-6 feature-icon">
                  <Icon size={22} className="text-[#00E5FF]" />
                </div>

                <h3
                  className="text-xl sm:text-2xl tracking-tight font-medium text-white mb-3"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {feature.title}
                </h3>
                <p
                  className="text-base leading-relaxed text-neutral-400 max-w-lg"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {feature.description}
                </p>

                {/* Decorative phone mockup for first card */}
                {feature.hasImage && (
                  <div className="absolute right-8 bottom-0 hidden lg:block opacity-20 group-hover:opacity-30 transition-opacity">
                    <img
                      src="https://images.pexels.com/photos/13570137/pexels-photo-13570137.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                      alt=""
                      className="h-48 w-auto object-contain"
                    />
                  </div>
                )}

                {/* Hover glow */}
                <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#00E5FF]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
